import { useEffect, useRef } from "react";
import { reaction } from "mobx";

import { useChatWidgetContext } from "@/components/Playground/Drawer/Chat/context";
import { usePlayground } from "@/components/Playground/playground-store";
import { displayErrorToast } from "@/utils/toast";

// Providers can spend over a minute reasoning before the first streamed token,
// and MCP/tool execution may be silent while awaiting a remote server.
const IDLE_TIMEOUT_MS = 120000;
const CHECK_INTERVAL_MS = 5000;

export const useIdleSafeGuard = () => {
  const { chatStore } = usePlayground();
  const { variableSetIndex } = useChatWidgetContext();

  const lastActivityTimeRef = useRef<number | null>(null);
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const stopIdleCheck = () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
    };

    const handleTimeout = () => {
      displayErrorToast("Request timed out. Please try again.");
      chatStore.setPendingRequestId(variableSetIndex, null);
      chatStore.setStreamingMessageId(variableSetIndex, null);
      stopIdleCheck();
    };

    const startIdleCheck = () => {
      stopIdleCheck();
      intervalIdRef.current = setInterval(() => {
        const lastActivity = lastActivityTimeRef.current;
        if (lastActivity && Date.now() - lastActivity > IDLE_TIMEOUT_MS) {
          handleTimeout();
        }
      }, CHECK_INTERVAL_MS);
    };

    // Track when pending request starts/stops for this variable set
    const disposePendingReaction = reaction(
      () => chatStore.getPendingRequestId(variableSetIndex),
      requestId => {
        if (requestId !== null) {
          // Request started - begin countdown
          lastActivityTimeRef.current = Date.now();
          startIdleCheck();
        } else {
          // Request finished - stop countdown
          stopIdleCheck();
          lastActivityTimeRef.current = null;
        }
      },
      { fireImmediately: true },
    );

    // Track socket activity - reset timer on each socket event
    const disposeSocketActivityReaction = reaction(
      () => chatStore.lastSocketActivity,
      activityTime => {
        if (activityTime !== null && chatStore.getPendingRequestId(variableSetIndex) !== null) {
          // Socket event received - reset countdown
          lastActivityTimeRef.current = activityTime;
        }
      },
    );

    return () => {
      disposePendingReaction();
      disposeSocketActivityReaction();
      stopIdleCheck();
    };
  }, [chatStore, variableSetIndex]);
};
